import { DocumentSection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/components/DocumentSection';
import { type DocumentWorkflowFileTct } from '@dealstack/shared/lib/generated';
import { TemplateSection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/components/TemplateSection';
import { TextV2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';
import { AlertDescription, Alert } from '@dealstack/frontend/shared/components/Alert/Alert';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { DOCUMENT_WORKFLOW } from '@dealstack/shared/tests/testElementIds';
import { Textarea, em } from '@mantine/core';
import { Dispatch, SetStateAction, useState } from 'react';
import ReactModal from 'react-modal';
import styled from 'styled-components';
import {
	Counterparty,
	DocumentSettings,
	DocumentSettingsFieldNames,
	TemplateTypesOptions,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/types';
import { DataFileTct, DocumentTemplateType } from '@dealstack/shared/lib/generated';
import { TemplatePredefinedOptions } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/templatePredefinedOptions';
import { SortingOrder } from '@dealstack/shared/lib/frontend/common-ui/types';
import { hasOverlappingPendingPlacements } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/placement/hasOverlappingPendingPlacements';

interface Props {
	className?: string;
	numberOfInvestors: number;
	selectedInvestorsIds: string[];
	emailTextValue: string;
	selectedDocumentsReadyToSend: DocumentSettings[];
	documentsDataArray: DocumentSettings[];
	documentFiles: DocumentWorkflowFileTct[];
	documentsFilesSearch: string | undefined;
	documentsFilesSorting: SortingOrder;
	showSelectedDocuments: boolean;
	openedDropdownDocumentCardId: string | undefined;
	activeCheckboxesIds: string[];
	onClickDropdown: (documentWorkflowFileId: string) => void;
	onCloseDropdown: () => void;
	toggleCheckboxState: (documentWorkflowFileId: string) => void;
	setShowSelectedDocuments: Dispatch<SetStateAction<boolean>>;
	setDocumentsFilesSearch: Dispatch<SetStateAction<string | undefined>>;
	setDocumentsFilesSorting: Dispatch<SetStateAction<SortingOrder>>;
	setDocumentsDataArray: Dispatch<SetStateAction<DocumentSettings[]>>;
	onClose: () => void;
	onClickSendDocuments: () => void;
	setEmailTextValue: (value: string) => void;
}

export const SendDocumentModalContent = ({
	className,
	emailTextValue,
	numberOfInvestors,
	documentsDataArray,
	selectedDocumentsReadyToSend,
	documentFiles,
	documentsFilesSearch,
	documentsFilesSorting,
	showSelectedDocuments,
	openedDropdownDocumentCardId,
	activeCheckboxesIds,
	onClickDropdown,
	onCloseDropdown,
	toggleCheckboxState,
	setShowSelectedDocuments,
	setDocumentsDataArray,
	onClose,
	onClickSendDocuments,
	setEmailTextValue,
	setDocumentsFilesSearch,
	setDocumentsFilesSorting,
}: Props) => {
	const [isUploadingGuidanceNote, setIsUploadingGuidanceNote] = useState<boolean>(false);
	const [isTextAreaFocused, setIsTextAreaFocused] = useState(false);
	const [templateTypeToChange, setTemplateTypeToChange] = useState<string>('');
	const [selectedDocumentWorkflowId, setSelectedDocumentWorkflowId] = useState<string>('');
	const [counterpartyToDelete, setCounterpartyToDelete] = useState<{
		chipId: string;
		name: string;
	}>({
		chipId: '',
		name: '',
	});
	const showCounterpartyDeleteAlert = !!counterpartyToDelete.chipId;
	// Gated here, not in the placement editor: every way out of the editor leaves its boxes on the document.
	const hasOverlappingPlacements = selectedDocumentsReadyToSend.some(
		(document) =>
			document.useManualSignaturePlacement &&
			hasOverlappingPendingPlacements(document.signaturePlacements ?? [])
	);

	const onCloseCounterpartyDeleteAlert = () => {
		setCounterpartyToDelete({ chipId: '', name: '' });
	};

	const onCloseChangeTemplateAlert = () => {
		setTemplateTypeToChange('');
	};

	const changeDocumentTemplate = () => {
		if (templateTypeToChange) {
			changeDocumentTemplateType(selectedDocumentWorkflowId, templateTypeToChange);
			setTemplateTypeToChange('');
		}
	};

	const autoTemplateTypes: Set<string> = new Set([
		DocumentTemplateType.GDPR_NOTICE,
		DocumentTemplateType.FORM_S431,
		DocumentTemplateType.ANNUAL_REPORT,
		DocumentTemplateType.PRE_FILLABLE_FORM,
	]);

	const onDataChange = (
		selectedDocumentWorkflowFileId: string,
		changedField: DocumentSettingsFieldNames,
		value: string | boolean | DataFileTct | undefined
	) => {
		setDocumentsDataArray((documentTemplateData: DocumentSettings[]) => {
			return documentTemplateData.map((documentTemplate) => {
				if (documentTemplate.documentWorkflowFileId !== selectedDocumentWorkflowFileId) {
					return documentTemplate;
				}
				const shouldPreserveTemplateType =
					changedField === 'guidanceNote' ||
					changedField === 'isToBeSubmitted' ||
					autoTemplateTypes.has(documentTemplate.templateType ?? '');
				return {
					...documentTemplate,
					templateType: shouldPreserveTemplateType
						? documentTemplate.templateType
						: DocumentTemplateType.CUSTOM,
					[changedField]: value,
				};
			});
		});
	};

	const tMipManage = useTranslationMipManage();
	const tPrefix = 'investors.simple_document_workflow.template_section.alert_modal';
	const titleTranslationKey =
		numberOfInvestors > 1
			? 'investors.simple_document_workflow.investorsSelectedWithCount_other'
			: 'investors.simple_document_workflow.investorsSelectedWithCount_one';

	const sendDocumentModalTitleText = tMipManage(titleTranslationKey, {
		numberOfInvestors,
	});

	const alertDescriptionText = showCounterpartyDeleteAlert
		? tMipManage(`${tPrefix}.delete_counterparty`, {
				counterpartyName: counterpartyToDelete.name,
			})
		: tMipManage(`${tPrefix}.change_template_type`);

	const isDocumentInDataArray = (selectedDocumentWorkflowFileId: string) => {
		return !!documentsDataArray?.find(
			(document) => document.documentWorkflowFileId === selectedDocumentWorkflowFileId
		);
	};

	const changeDocumentTemplateType = (
		selectedDocumentWorkflowFileId: string,
		templateType: TemplateTypesOptions['name']
	) => {
		const newElement: DocumentSettings = {
			...TemplatePredefinedOptions[templateType],
			documentWorkflowFileId: selectedDocumentWorkflowFileId,
		};
		setDocumentsDataArray((documentTemplateData) => {
			const hasElement = isDocumentInDataArray(selectedDocumentWorkflowFileId);
			if (!hasElement) {
				return [...documentTemplateData, newElement];
			}

			return documentTemplateData.map((document) => {
				if (
					document.documentWorkflowFileId !== selectedDocumentWorkflowFileId ||
					(templateType === DocumentTemplateType.GDPR_NOTICE &&
						document.templateType !== DocumentTemplateType.GDPR_NOTICE) ||
					(templateType === DocumentTemplateType.PRE_FILLABLE_FORM &&
						document.templateType !== DocumentTemplateType.PRE_FILLABLE_FORM) ||
					document.templateType === templateType ||
					autoTemplateTypes.has(document.templateType ?? '')
				) {
					return document;
				}

				return {
					...newElement,
					counterparties: document.counterparties,
					guidanceNote: document.guidanceNote,
				};
			});
		});
	};

	const DEFAULT_DATA = { ...TemplatePredefinedOptions.CUSTOM, templateType: undefined };

	const currentDocumentData =
		documentsDataArray.find(
			(document) => document.documentWorkflowFileId === selectedDocumentWorkflowId
		) ?? DEFAULT_DATA;

	const removeCounterparty = () => {
		const updatedDocumentsDataArray = (documentTemplateData: DocumentSettings[]) => {
			return documentTemplateData.map((documentTemplate) => {
				if (
					documentTemplate.documentWorkflowFileId !==
					currentDocumentData.documentWorkflowFileId
				) {
					return documentTemplate;
				}
				return {
					...documentTemplate,
					counterparties: removeCounterpartyFromList(documentTemplate.counterparties),
				};
			});
		};

		const removeCounterpartyFromList = (counterparties: Counterparty[]) => {
			return counterparties.filter(
				(counterparty) => counterparty.chipId !== counterpartyToDelete.chipId
			);
		};

		setDocumentsDataArray(updatedDocumentsDataArray);
		setCounterpartyToDelete({ chipId: '', name: '' });
	};

	return (
		<ContentView className={className} data-testid={DOCUMENT_WORKFLOW.MODAL}>
			<TopWrapper>
				<TextV2 variant="sans/17/800" data-testid={DOCUMENT_WORKFLOW.TITLE}>
					{sendDocumentModalTitleText}
				</TextV2>
				<Icon variant="close-v3" onClick={onClose} data-testid={DOCUMENT_WORKFLOW.CLOSE} />
			</TopWrapper>
			<DocumentSection
				isUploadingGuidanceNote={isUploadingGuidanceNote}
				documentsDataArray={documentsDataArray}
				selectedDocumentWorkflowId={selectedDocumentWorkflowId}
				setDocumentsDataArray={setDocumentsDataArray}
				setSelectedDocumentWorkflowId={setSelectedDocumentWorkflowId}
				isDocumentInDataArray={isDocumentInDataArray}
				changeDocumentTemplateType={changeDocumentTemplateType}
				documentFiles={documentFiles}
				documentsFilesSearch={documentsFilesSearch}
				documentsFilesSorting={documentsFilesSorting}
				setDocumentsFilesSearch={setDocumentsFilesSearch}
				setDocumentsFilesSorting={setDocumentsFilesSorting}
				showSelectedDocuments={showSelectedDocuments}
				setShowSelectedDocuments={setShowSelectedDocuments}
				onClickDropdown={onClickDropdown}
				openedDropdownDocumentCardId={openedDropdownDocumentCardId}
				onCloseDropdown={onCloseDropdown}
				toggleCheckboxState={toggleCheckboxState}
				activeCheckboxesIds={activeCheckboxesIds}
			/>
			<TemplateSection
				setTemplateTypeToChange={setTemplateTypeToChange}
				onDataChange={onDataChange}
				isUploadingGuidanceNote={isUploadingGuidanceNote}
				setIsUploadingGuidanceNote={setIsUploadingGuidanceNote}
				changeDocumentTemplateType={changeDocumentTemplateType}
				selectedDocumentWorkflowId={selectedDocumentWorkflowId}
				documentFiles={documentFiles}
				documentsDataArray={documentsDataArray}
				setDocumentsDataArray={setDocumentsDataArray}
				setCounterpartyToDelete={setCounterpartyToDelete}
			/>
			<BottomContentWrapper>
				<Textarea
					data-testid={DOCUMENT_WORKFLOW.CUSTOM_MESSAGE_INPUT}
					placeholder={tMipManage(
						'investors.simple_document_workflow.template_section.textarea_placeholder'
					)}
					leftSection={<Icon variant="pencil-edit-line" />}
					autosize
					minRows={isTextAreaFocused ? 3 : 1}
					sx={{ width: '100%' }}
					onFocus={() => {
						setIsTextAreaFocused(true);
					}}
					onBlur={() => {
						setIsTextAreaFocused(false);
					}}
					value={emailTextValue}
					onChange={(event) => {
						setEmailTextValue(event.currentTarget.value);
					}}
				/>
				<ButtonV2
					data-testid={DOCUMENT_WORKFLOW.SEND_DOCUMENT_BUTTON}
					size="s"
					icon="arrow-right"
					buttonTheme="light"
					variant="primary"
					rightIcon
					onClick={onClickSendDocuments}
					disabled={
						isUploadingGuidanceNote ||
						!selectedDocumentsReadyToSend.length ||
						hasOverlappingPlacements
					}
				>
					{tMipManage('investors.simple_document_workflow.send_documents_button')}
				</ButtonV2>
			</BottomContentWrapper>
			{(showCounterpartyDeleteAlert || templateTypeToChange) && (
				<AlertModal contentLabel="alert" isOpen className={className}>
					<Alert icon="tick-circle-filled" state="success" size="s">
						<AlertDescription>{alertDescriptionText}</AlertDescription>
						<AlertButtonWrapper>
							<ButtonV2
								size="s"
								icon="close-v3"
								buttonTheme="light"
								variant="secondary"
								onClick={
									showCounterpartyDeleteAlert
										? removeCounterparty
										: changeDocumentTemplate
								}
							>
								{tMipManage(`${tPrefix}.yes_button`)}
							</ButtonV2>

							<ButtonV2
								size="s"
								icon="close-v3"
								buttonTheme="light"
								variant="ghost"
								onClick={
									showCounterpartyDeleteAlert
										? onCloseCounterpartyDeleteAlert
										: onCloseChangeTemplateAlert
								}
							>
								{tMipManage(`${tPrefix}.no_button`)}
							</ButtonV2>
						</AlertButtonWrapper>
					</Alert>
				</AlertModal>
			)}
		</ContentView>
	);
};

const AlertModal = styled(ReactModal)`
	background-color: ${defaultTheme.v2.color.white[500]};
	border-radius: ${em(8)};
`;

const AlertButtonWrapper = styled.div`
	display: flex;
	gap: 0.5rem;
`;

const TopWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	margin-bottom: 1em;
	${Text}, ${Icon} {
		color: ${defaultTheme.v2.color.neutral[1100]};
	}
`;

const BottomContentWrapper = styled.div`
	display: flex;
	gap: 0.5rem;
	align-items: center;
	margin-top: ${em(16)};
	${ButtonV2} {
		margin-left: auto;
		width: 10rem;
	}
	.mantine-Textarea-input {
		margin: 0;
		box-sizing: border-box;
		min-height: 32px;
	}
`;

const ContentView = styled.div`
	padding: 1rem;
`;
